import { activateOffer, confirmFirstCharge, fetchOffer, fetchWalletBudget, retryOffer, saveManageToken, signedTermsStep, waitForSubscriptionStatus } from "./api";
import { emitRecurringEvent } from "./events";
import { applyBrandColor } from "./storefront-app";
import { bindManage, bindManageLink, isRetryable, manageBody, manageLinkSlot } from "./manage";
import type { SubscriptionOffer } from "./types";
import {
  amountHero,
  chargeQuote,
  chipRow,
  dangerButton,
  errorBox,
  hint,
  loadingRow,
  paymentShell,
  primaryButton,
  quietButton,
} from "./ui";
import { SAVING_WAIT } from "./retry";
import { connectAndApprove, connectedWallet, InsufficientFundsError, prefetchWallet, setWalletScope, signOutWallet } from "./wallet";

function idFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("id")?.trim() || null;
}

function offerChips(offer: SubscriptionOffer): string {
  const items: Array<{ icon: string; label: string }> = [];
  if (offer.icon_coin_url && offer.token_symbol) {
    items.push({ icon: offer.icon_coin_url, label: offer.token_symbol });
  }
  const net = offer.short_name || offer.network_name;
  if (offer.icon_network_url && net) {
    items.push({ icon: offer.icon_network_url, label: net });
  }
  return chipRow(items);
}

export class SubscribeApp {
  private busy = false;

  constructor(private root: HTMLElement) {
    this.root.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest("#sign-out-wallet")) return;
      void signOutWallet();
      this.root.querySelector(".wallet-bar")?.remove();
    });
  }

  private shell(offer: SubscriptionOffer, opts: { title?: string; subtitle?: string; body: string }): string {
    applyBrandColor(offer.color);
    return paymentShell({
      title: opts.title || offer.title,
      subtitle: opts.subtitle,
      body: opts.body,
      logoUrl: offer.logo_url,
      brandName: offer.pos_name || undefined,
      wallet: connectedWallet(),
    });
  }

  async init(): Promise<void> {
    const id = idFromUrl();
    if (!id) {
      this.root.innerHTML = paymentShell({
        title: "Subscription",
        body: errorBox("Open this page as /subscribe?id=…"),
      });
      return;
    }
    this.root.innerHTML = paymentShell({
      title: "Subscription",
      body: loadingRow("Loading…"),
    });
    prefetchWallet();
    const token = new URLSearchParams(window.location.search).get("token")?.trim() || "";
    if (token) {
      saveManageToken(id, token);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("token");
      window.history.replaceState({}, "", clean.toString());
    }
    try {
      const offer = await fetchOffer(id);
      setWalletScope(offer.reference || offer.subscription_id);
      if (offer.status === "awaiting_charge") {
        this.render(offer);
        return;
      }
      if (offer.status === "past_due" && offer.can_manage !== false) {
        await this.retryInBackground(offer);
        return;
      }
      this.render(offer);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to load offer";
      this.root.innerHTML = paymentShell({
        title: "Subscription",
        body: errorBox(msg),
      });
    }
  }

  private render(offer: SubscriptionOffer, extra = "", action = "Connect wallet"): void {
    if (offer.status === "awaiting_charge") {
      const quote = (offer.charges || []).find((charge) => charge.status === "quoted" && charge.invoice_amount);
      const charged = quote?.invoice_amount ? `${quote.invoice_amount} ${offer.token_symbol}` : "";
      this.root.innerHTML = this.shell(offer, {
        title: offer.title,
        subtitle: "First payment",
        body: charged
          ? `
          ${amountHero(`${offer.amount_fiat} ${offer.currency_fiat}`, "Subscription amount")}
          ${offerChips(offer)}
          ${chargeQuote(charged)}
          ${hint(`This rate is for the current ${offer.amount_fiat} ${offer.currency_fiat} charge. Later charges can change with the exchange rate.`)}
          ${offer.can_manage === false ? manageLinkSlot(offer.subscription_id) : `${primaryButton("charge-now", "Charge now")}${dangerButton("cancel-sub", "Cancel subscription")}`}
          ${extra}
        `
          : `
          ${errorBox(offer.last_error || "The invoice is not ready yet.")}
          ${offer.can_manage === false ? manageLinkSlot(offer.subscription_id) : primaryButton("charge-now", "Try again")}
          ${extra}
        `,
      });
      this.root.querySelector("#charge-now")?.addEventListener("click", () => {
        void this.confirmCharge(offer);
      });
      bindManage(this.root, offer, (next) => this.render(next));
      return;
    }
    const waiting = offer.status === "confirming";
    const managed = ["active", "past_due", "exhausted", "cancelled"].includes(offer.status);
    const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
    if (waiting) {
      this.root.innerHTML = this.shell(offer, {
        title: offer.title,
        subtitle: "First payment",
        body: `
          ${amountHero(amount, "Subscription amount")}
          ${offerChips(offer)}
          ${loadingRow("Waiting for blockchain confirmation…")}
          ${hint("This can take a minute. Don’t close this window.")}
          ${extra}
        `,
      });
      return;
    }
    if (managed) {
      this.root.innerHTML = this.shell(offer, {
        title: offer.title,
        subtitle: isRetryable(offer)
          ? `Retry the ${offer.token_symbol} payment`
          : `Manage your ${offer.token_symbol} subscription`,
        body: isRetryable(offer)
          ? `${amountHero(amount, "Subscription amount")}
            ${offerChips(offer)}
            ${manageBody(offer, extra)}`
          : manageBody(offer, extra),
      });
      bindManage(this.root, offer, (next) => {
        this.render(next);
        if (next.status === "confirming") {
          void this.waitUntilSettled(next);
        }
      });
      return;
    }

    const locked = offer.can_manage === false;
    this.root.innerHTML = this.shell(offer, {
      title: offer.title,
      subtitle: `Approve a capped ${offer.token_symbol} allowance`,
      body: `
        ${amountHero(amount, "Subscription amount")}
        ${offerChips(offer)}
        ${locked ? manageLinkSlot(offer.subscription_id) : primaryButton("go", action)}
        ${extra}
      `,
    });
    if (locked) {
      bindManageLink(this.root);
      return;
    }
    this.root.querySelector("#go")?.addEventListener("click", () => {
      void this.approve(offer);
    });
  }

  private async confirmCharge(offer: SubscriptionOffer): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const button = this.root.querySelector<HTMLButtonElement>("#charge-now");
    if (button) {
      button.disabled = true;
      button.textContent = button.textContent === "Try again" ? "Creating the invoice…" : "Sending the payment…";
    }
    try {
      const next = await confirmFirstCharge(offer.subscription_id);
      if (next.status === "confirming") {
        this.render(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.render(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Charge failed";
      this.render(offer, errorBox(msg));
    }
  }

  private showInsufficient(offer: SubscriptionOffer, message: string): void {
    const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
    this.root.innerHTML = this.shell(offer, {
      title: offer.title,
      subtitle: `Approve a capped ${offer.token_symbol} allowance`,
      body: `
        ${amountHero(amount, "Subscription amount")}
        ${offerChips(offer)}
        ${errorBox(message)}
        ${primaryButton("switch-wallet", "Connect another wallet")}
        ${quietButton("retry-approve", "Try again")}
      `,
    });
    this.root.querySelector("#switch-wallet")?.addEventListener("click", () => {
      void this.approve(offer, true);
      this.root.querySelector(".wallet-bar")?.remove();
    });
    this.root.querySelector("#retry-approve")?.addEventListener("click", () => {
      void this.approve(offer);
    });
  }

  private async approve(offer: SubscriptionOffer, switchWallet = false): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.render(offer, loadingRow("Connect a wallet and confirm approve…"));
    try {
      const { payer, tx, signature } = await connectAndApprove({
        chainId: offer.chain_id,
        token: offer.token_contract,
        spender: offer.spender,
        amountRaw: BigInt(offer.approve_amount_raw),
        freshWallet: switchWallet,
        activation: {
          paymentAmount: offer.approve_amount,
          tokenDecimals: offer.token_decimals,
          tokenSymbol: offer.token_symbol,
        },
        otherBudgets: (payer) => fetchWalletBudget(offer.subscription_id, payer),
        ...signedTermsStep(offer),
        onStatus: (message) => {
          this.render(offer, loadingRow(message));
        },
      });
      this.render(offer, loadingRow("Saving the subscription…"));
      const next = await activateOffer(offer.subscription_id, payer, tx, signature, () => {
        this.render(offer, loadingRow(SAVING_WAIT));
      });
      if (next.status === "awaiting_charge") {
        this.busy = false;
        this.render(next);
        return;
      }
      if (next.status === "confirming") {
        emitRecurringEvent("subscription.confirming", next);
        this.render(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.render(next);
    } catch (error) {
      this.busy = false;
      if (error instanceof InsufficientFundsError) {
        this.showInsufficient(offer, error.message);
        return;
      }
      const msg = error instanceof Error ? error.message : "Approve failed";
      this.render(offer, errorBox(msg), "Try again");
    }
  }

  private async retryInBackground(offer: SubscriptionOffer): Promise<void> {
    const preparing = (offer.cycles_charged || 0) === 0;
    this.render(offer, loadingRow(preparing ? "Preparing the charge…" : "Trying the payment again…"));
    try {
      const next = await retryOffer(offer.subscription_id);
      if (next.status === "confirming") {
        this.render(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.render(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Could not retry payment";
      this.render(offer, errorBox(msg));
    }
  }

  private async waitUntilSettled(offer: SubscriptionOffer): Promise<void> {
    try {
      const paid = await waitForSubscriptionStatus(offer.subscription_id, (item) =>
        ["active", "exhausted", "past_due", "cancelled"].includes(item.status)
      );
      this.busy = false;
      if (paid.status === "cancelled") {
        emitRecurringEvent("subscription.failed", paid);
        this.render(paid, errorBox(paid.last_error || "Payment was not confirmed"));
        return;
      }
      if (paid.status === "past_due") {
        emitRecurringEvent("subscription.failed", paid);
        this.render(paid);
        return;
      }
      emitRecurringEvent("subscription.active", paid);
      this.render(paid);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Confirmation timed out";
      this.render(offer, errorBox(msg));
    }
  }
}
